import { Box, Image, Typography } from "@avenue-8/sidekick-ui";
import {
  HeaderSectionView,
  PresentationView,
} from "@cma/presentation-generation-logic";
import { HeaderBrandingSectionView } from "@cma/presentation-generation-logic/sections/header-branding/header-branding-section-view";
import { PresentationSectionViewProps } from "../PresentationSectionProps";
import { useMemo } from "react";
import { textFormatter } from "@shared/utils/text-formatter";
import { PresentationNavbar } from "../../shared/PresentationNavbar";
import styled from "@emotion/styled";
import { PrintPage, PrintPaper } from "../../shared/Print.styles";

type Props = PresentationSectionViewProps<
  HeaderSectionView | HeaderBrandingSectionView
> & {
  agent: PresentationView["agent"];
};

const Page1Header = styled.div`
  flex: 0 0 auto;
`;

const Page1Content = styled.div`
  flex: 1 1 auto;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 0.5rem;
  overflow: hidden;
`;

const PreparedForContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 0.25rem;
  margin-top: 2rem;
  min-height: 2rem;
  flex: 0 0 auto;
`;

const AddressContainer = styled.div`
  text-align: center;
  margin: 1rem auto;
  flex: 0 0 auto;
`;

const CoverPhotoContainer = styled.div`
  flex: 1 1 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0.5rem 0;
  width: 100%;
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: ${(p) => p.theme.colors.background2};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const PriceContainer = styled.div`
  text-align: center;
  margin: 0.5rem 0;
  padding: 0.5rem;
`;

const GalleryGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  grid-template-rows: repeat(4, 1fr);
  gap: 0.5rem;
  width: 100%;
  flex: 1;
  min-height: 0;
`;

const GalleryItem = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.25rem;
  overflow: hidden;
  background-color: ${(p) => p.theme.colors.background2};

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EmptyGalleryPlaceholder = styled.div`
  width: 100%;
  height: 100%;
  background-color: #ffffff;
`;

export const HeroHeaderPrint = (props: Props) => {
  const { sectionView, agent } = props;
  const { type, clientName, addressLine1, photos, minSellPrice, maxSellPrice } =
    sectionView;

  const officeLogo = type === "header-branding" ? sectionView.officeLogo : null;

  // Get first 12 photos for gallery and pad to 12 items
  const paddedGalleryPhotos = useMemo(() => {
    const galleryPhotos = photos?.slice(1, 13) || [];
    const result: Array<(typeof galleryPhotos)[0] | null> = [...galleryPhotos];
    while (result.length < 12) {
      result.push(null);
    }
    return result.slice(0, 12);
  }, [photos]);

  return (
    <>
      <PrintPaper>
        <PrintPage>
          <Page1Header>
            <PresentationNavbar
              agent={agent}
              officeLogo={officeLogo}
              displayMode="print"
            />
          </Page1Header>

          <Page1Content>
            <PreparedForContainer>
              <Typography>Prepared for</Typography>
              <Typography fontWeight={600}>{clientName}</Typography>
            </PreparedForContainer>

            <AddressContainer>
              <Typography variant="h5" fontWeight={600}>
                {addressLine1}
              </Typography>
            </AddressContainer>

            {photos && photos.length > 0 && (
              <CoverPhotoContainer>
                <Image src={photos[0].url} alt="Cover Photo" />
              </CoverPhotoContainer>
            )}

            {!!minSellPrice && !!maxSellPrice && (
              <PriceContainer>
                <Typography variant="body2">
                  In today&apos;s market, this home should sell for between
                </Typography>
                <Box display="flex" justifyContent="center" gap={0.75}>
                  <Typography fontWeight={600}>
                    {textFormatter.currency(minSellPrice)}
                  </Typography>
                  <Typography>and</Typography>
                  <Typography fontWeight={600}>
                    {textFormatter.currency(maxSellPrice)}
                  </Typography>
                </Box>
              </PriceContainer>
            )}
          </Page1Content>
        </PrintPage>
      </PrintPaper>

      <PrintPaper>
        <PrintPage>
          <Typography variant="h5" mb={2} textAlign={"center"}>
            Photos
          </Typography>

          <GalleryGrid>
            {paddedGalleryPhotos.map((photo, index) => (
              <GalleryItem key={`gallery-${index}`}>
                {photo ? (
                  <Image src={photo.url} alt={`Property photo ${index + 2}`} />
                ) : (
                  <EmptyGalleryPlaceholder />
                )}
              </GalleryItem>
            ))}
          </GalleryGrid>
        </PrintPage>
      </PrintPaper>
    </>
  );
};
